/**
 * 本期买家流的纯逻辑。设计定稿见 `doc/design/MILHAR_ATMOSPHERE_SPEC.md`。
 *
 * <p>放在这里而不是组件里，是因为这三件事都能在没有 DOM 的情况下讲清楚，
 * 而它们恰好是最容易悄悄坏掉的部分：播放顺序、注入额、相对时间。
 */

/**
 * 轮播真正依赖的三个字段。
 *
 * <p>播放顺序、去重、水位线、积压追赶这四件事**只碰这三个字段** —— 泛型化不是
 * 为了好看：大厅的混流行（`shared/hall/betFeedView.ts`）比 Milhar 的多带
 * game/tab/currency 等等，把它们硬塞成 `FeedOrder` 才是类型撒谎。
 */
export type TickerRow = {
  id: number;
  placedAt: number;
  teimosinha: boolean;
};

export type FeedOrder = {
  /** 注单 id。**播放去重只认它** —— 见 `unseenOrders`。 */
  id: number;
  userId: number;
  username: string;
  avatarDefaultId?: number | null;
  avatarThumbUrl?: string | null;
  vipLevel?: number | null;
  pick: string;
  /**
   * 注额。**是字符串**，形如 `"5.00"` —— 后端所有金额都走 `money()` 序列化，
   * 这是仓库惯例（见 `pool.takeHome`、`myBets[].amount`）。
   *
   * <p>别把它声明成 `number`：类型撒谎不会有人报错，但
   * `Number.isFinite("5.00")` 是 **false**，于是注入额一路算成 0，
   * 线上每一笔都飘 `+R$ 0`。
   */
  amount: string;
  /** 下单时刻，秒。 */
  placedAt: number;
  /** 自动续期票属于本期买家名单，但不是此刻发生的下单，不能进入 ticker。 */
  teimosinha: boolean;
};

export type RoundFeed = {
  orders: FeedOrder[];
  people: number;
  tickets: number;
  injected: string;
};

/**
 * 轮播同时可见的条数。定稿 §3 第 3 点原为 3，2026-08-19 评审加到 5。
 *
 * <p>加行不只是「多看两条」：窗口越浅，一条单停留的时间越短，冷清时段整块社交区
 * 越容易在两条之间空掉。5 行在 375px 上仍然不挤到把选号器推出首屏。
 */
export const FEED_WINDOW = 5;

/** 换条的基础间隔，队列不积压时就是它。 */
export const FEED_ROTATE_MS = 3000;

/** 追赶时的下限。再快就不是「在播」而是在刷屏，人眼读不完一行。 */
export const FEED_ROTATE_MIN_MS = 600;

/**
 * 下一条该等多久 —— 按队列深度自适应。
 *
 * <p><b>问题</b>：页面上有两个时钟。人数和头像带是轮询到就整批更新（5s），而下面的
 * 记录是一条一条挤进来的（3s 一条）。于是一次轮询回来 8 笔新单，头像瞬间多 8 个，
 * 下面要 24 秒才播完 —— 而且积压只会越拉越大，永远追不上。用户看到的就是
 * 「头像一直在涨，下面反应很慢」。
 *
 * <p><b>不能直接把间隔调小</b>：冷清时段本来就一条一条来，统一调快只会让唯一的
 * 那条单一闪而过。所以间隔跟着积压走 —— 没积压时保持 3s 的呼吸感，积压越深越快，
 * 封底 {@link FEED_ROTATE_MIN_MS}。
 *
 * <p>用**整除**而不是指数衰减：3000/深度 在深度 1..5 之间给出 3000→600 的自然梯度，
 * 再深也就贴着下限走，行为可预测，也容易在测试里钉住。
 */
export function drainDelayMs(queueDepth: number): number {
  const depth = Number.isFinite(queueDepth) ? Math.floor(queueDepth) : 0;
  if (depth <= 1) return FEED_ROTATE_MS;
  return Math.max(FEED_ROTATE_MIN_MS, Math.round(FEED_ROTATE_MS / depth));
}

/** ticker 只展示此刻发生的下单；teimosinha 自动续期票仍保留在买家名单。 */
export function tickerOrders<T extends TickerRow>(orders: readonly T[]): T[] {
  return orders.filter((order) => !order.teimosinha);
}

/**
 * 一笔投注让**屏幕上那个奖池数字**涨多少。
 *
 * <p>注意它不是「进池多少」：屏幕上显示的是 `takeHome`（池子 × (1 - 留存比例)），
 * 所以 R$2 的注里 25% = R$0.50 进池，而那个数字只涨 R$0.40。标的是「这个数字涨了
 * 多少」，不是「进池多少」—— 两者不同是对的，因为它就贴在那个数字旁边。
 *
 * <p><b>两个比例都必须来自 config，不许写死。</b>原来这里写着 `× 0.25 × 0.8`，
 * 于是调整进池比例时动画会继续按旧比例飘，而且没有任何地方会报错
 * （2026-08-20 评审）。配置项发的是已 ×100 的整数（"25" / "20"）。
 *
 * @param contribPct   进池比例，已 ×100（config.jackpotContribPct）
 * @param seedRetainPct 开奖时留作下期种子的比例，已 ×100（config.seedRetainPct）
 */
export function injectionFromAmount(
  amount: string | number,
  contribPct: string | number | undefined,
  seedRetainPct: string | number | undefined,
): number {
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value) || value <= 0) return 0;
  const contrib = Number(contribPct);
  const retain = Number(seedRetainPct);
  // 配置缺席或不合理时返回 0：宁可不飘，也不要飘一个编出来的数字
  if (!Number.isFinite(contrib) || contrib <= 0 || contrib > 100) return 0;
  if (!Number.isFinite(retain) || retain < 0 || retain >= 100) return 0;
  // 按分取整。这是钱，而且它会和旁边那个到分的奖池数字并排显示 ——
  // 浮点残差（2 × 0.2 × 0.8 = 0.32000000000000006）不该有机会走到格式化那一步。
  return Math.round(value * (contrib / 100) * (1 - retain / 100) * 100) / 100;
}

/**
 * 轮询返回里还没播过的那些，**按时间正序**返回（先发生的先播）。
 *
 * <p>接口每 5 秒给最近 15 条，而轮播基础节奏是 3 秒一条（积压时更快，见
 * `drainDelayMs`）—— 两者节奏不同，所以必须由
 * 客户端自己记「播到哪了」。用 id 而不是下标：下标会随新单插入而整体平移，
 * 那会导致刚播过的条目再播一遍。
 */
export function unseenOrders<T extends TickerRow>(
  orders: T[],
  seenIds: ReadonlySet<number>,
  /**
   * 只播不早于这个时刻的单。传首帧填充里最新那条的 `placedAt`。
   *
   * <p>**没有这个水位线就会回放历史**：首帧填满的是最近的那批，而队列若从「所有
   * 没播过的」里挑，最老的那条会第一个被推上去 —— 屏幕上就出现一条 3 分钟前的
   * 单压在 6 秒前的单上面。轮播叫「最新下单」，倒着走会把它变成一个假的活跃度。
   */
  sincePlacedAt = 0,
): T[] {
  return orders
    .filter((order) => !seenIds.has(order.id) && order.placedAt >= sincePlacedAt)
    .slice()
    .sort((a, b) => a.placedAt - b.placedAt || a.id - b.id);
}

/** 首帧填充里最新那条的时刻，即后续轮播的水位线。空列表返回 0。 */
export function feedWatermark(seeded: readonly TickerRow[]): number {
  return seeded.reduce((max, o) => (o.placedAt > max ? o.placedAt : max), 0);
}

/**
 * 把一条推进可见窗口，最新的在最前。
 *
 * <p>同 id 重复推入时**原地提到最前而不是留两份** —— 轮询重叠时同一条可能
 * 被算作未播两次，留两份会让同一个人在屏幕上出现两行。
 */
export function pushFeed<T extends TickerRow>(
  buffer: readonly T[],
  next: T,
  window = FEED_WINDOW,
): T[] {
  const rest = buffer.filter((o) => o.id !== next.id);
  return [next, ...rest].slice(0, window);
}

/**
 * 初始缓冲。入参是接口给的**倒序**列表（最新在前）。
 *
 * <p>两条约束，缺一不可：
 *
 * <p><b>① 直接填满窗口，不要从一条开始长。</b> 定稿 §3：否则社交区会从 1 行一路
 * 长到 FEED_WINDOW 行，把整页往下推 —— 用户正在选号时被推走一段。
 *
 * <p><b>② 取的是最近窗口里最老的那几条，不是最新的几条。</b> 取最新的会让轮播
 * 无事可播：剩下的全比它们老，按时序不该盖上去，于是只有真的来了新单才会动 ——
 * 冷清的一期社交区就是一动不动的，进页面头几秒也不动。从最老的起播，一进来就有
 * 整批可放，而且**每条新上来的都比上一条新**，时序自洽。
 */
export function seedFeed<T extends TickerRow>(orders: T[], window = FEED_WINDOW): T[] {
  return orders.slice(-window);
}

/**
 * 相对时间。**只到分钟** —— 本期最长也就几分钟，给到小时是没有意义的精度。
 *
 * @returns i18n 模板的参数；`unit` 为 `now` 时模板不带数字
 */
export function agoParts(placedAtSec: number, nowSec: number): { unit: 'now' | 's' | 'min'; value: number } {
  const diff = Math.max(0, Math.floor(nowSec - placedAtSec));
  if (diff < 5) return { unit: 'now', value: 0 };
  if (diff < 60) return { unit: 's', value: diff };
  return { unit: 'min', value: Math.floor(diff / 60) };
}

/**
 * 社交区该不该渲染。
 *
 * <p>定稿 §3.1：本期无人时**整块不渲染**，不占位、不骨架 ——
 * 空的社交模块比没有模块更伤氛围。沿用 §8.5.1① 的既有口径。
 */
export function hasFeed(feed: RoundFeed | null | undefined): feed is RoundFeed {
  return !!feed && Array.isArray(feed.orders) && feed.orders.length > 0 && feed.people > 0;
}
